import React from 'react';

// One inline notice, used by every request-creation path.
//
// ── What it is not ──────────────────────────────────────────────────────────
// NOT a toast system. There is no provider, no queue, no auto-dismiss and no
// portal. It renders where it is placed and stays until the caller stops
// rendering it. A global toast layer is a bigger change than the RFQ phase-2
// work should carry, and nothing here forecloses adding one later.
//
// ── Why it exists ───────────────────────────────────────────────────────────
// Before this, every surface invented its own: RequestQuoteModal used a bare
// <p className="rq-err">, RequestWizard and IdeaToProduct each held an `error`
// string, and Requests.jsx used window.alert() seven times over. RFQ phase 2
// needs a warning that is NOT an error — "your request was published, one file
// did not upload" — and there was no shape in the codebase that says that. A
// fifth idiom would have been the wrong answer to a problem caused by four.
//
// ── Text is the caller's ────────────────────────────────────────────────────
// This takes a finished string, never a translation key. Every caller already
// holds its own ar/en/zh copy block (`c`, `t`, or an inline ternary), and
// reaching into a fifth source from here would put one surface's wording out of
// reach of the file that owns it.

const TONES = {
  // Muted rather than loud. A published request with one missing file is not a
  // failure, and amber that shouts reads as one.
  warn: { bg: '#fdf6e3', bd: '#e2c88a', fg: '#7a5c15' },
  ok: { bg: '#eef7f0', bd: '#b7d9c0', fg: '#2d6a45' },
  error: { bg: '#fdeeee', bd: '#e6b3b3', fg: '#a05050' },
};

/**
 * @param {'ok'|'warn'|'error'} tone
 * @param {boolean} isAr   drives BOTH direction and font — the two travel together
 * @param {string}  children  finished, translated text
 */
export default function Notice({ tone = 'warn', isAr = false, children, style, ...rest }) {
  if (!children) return null;   // an empty notice is not a notice
  const t = TONES[tone] || TONES.warn;

  return (
    <div
      // role=status, not role=alert: this reports the outcome of something the
      // user just did. `alert` interrupts a screen reader mid-sentence, which is
      // right for a validation error and wrong for "published, with a caveat".
      role="status"
      dir={isAr ? 'rtl' : 'ltr'}
      style={{
        background: t.bg,
        border: `1px solid ${t.bd}`,
        color: t.fg,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 13,
        lineHeight: 1.7,
        // Logical property: this is the side the text starts on, which flips
        // with dir. `marginLeft` would put the accent on the wrong edge in
        // Arabic, which is the whole reason RTL bugs survive review.
        marginBlock: '10px 0',
        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
        textAlign: 'start',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
